import i18n, { LOCALIZATION_DEFAULTS } from '@assets/i18n/config';
import type { BareLocalization } from '@interfaces/localization';
import { LanguageCodes } from '@onevesthq/ov-enums';
import { languageCodeToLanguageMap } from '@onevesthq/ov-translations';
import { enumValues } from '@utils/enumValues';
import constate from 'constate';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useState } from 'react';

dayjs.extend(utc);

const languageCodeFromTag = (tag?: string) => {
  const base = tag?.split('-')[0];
  return enumValues(LanguageCodes).find((code) => code === base);
};

const activeI18nLanguage = () =>
  languageCodeFromTag(i18n.resolvedLanguage ?? i18n.language);

const withLocalizationDefaults = (
  localization?: Partial<BareLocalization> | null,
): BareLocalization => ({
  name: localization?.name || LOCALIZATION_DEFAULTS.name,
  countries: localization?.countries ?? LOCALIZATION_DEFAULTS.countries,
  phoneNumberCountryCodes:
    localization?.phoneNumberCountryCodes ??
    LOCALIZATION_DEFAULTS.phoneNumberCountryCodes,
  languages: localization?.languages ?? LOCALIZATION_DEFAULTS.languages,
  defaultCurrency:
    localization?.defaultCurrency || LOCALIZATION_DEFAULTS.defaultCurrency,
  defaultLanguage:
    localization?.defaultLanguage || LOCALIZATION_DEFAULTS.defaultLanguage,
  dateFormat: localization?.dateFormat || LOCALIZATION_DEFAULTS.dateFormat,
});

export const [UseLocalizationProvider, useLocalization] = constate(() => {
  const [applicableLocalization, setApplicableLocalizationState] =
    useState<BareLocalization>(() => withLocalizationDefaults());
  const [language, setLanguage] = useState<LanguageCodes>(
    () => activeI18nLanguage() ?? LanguageCodes.EN,
  );

  const setApplicableLocalization = useCallback(
    (localization?: Partial<BareLocalization> | null) => {
      setApplicableLocalizationState(withLocalizationDefaults(localization));
    },
    [],
  );

  const applicableLanguages = applicableLocalization.languages;

  const formattingMask = applicableLocalization.dateFormat || 'YYYY-MM-DD';

  const localizedDate = useCallback(
    (date: dayjs.ConfigType, convertToUTC = false, format?: string) => {
      if (!date) return ' - ';
      const parsed = convertToUTC ? dayjs(date).utc() : dayjs(date);
      return parsed.locale(language).format(format || formattingMask);
    },
    [formattingMask, language],
  );

  const localizedDateTime = useCallback(
    (dateTime: dayjs.ConfigType, convertToUTC = false, format?: string) => {
      const parsed = convertToUTC ? dayjs(dateTime).utc() : dayjs(dateTime);
      return parsed
        .locale(language)
        .format(format || `${formattingMask} hh:mma`);
    },
    [formattingMask, language],
  );

  const translateBackendString = useCallback(
    (translatedObject: unknown): string => {
      if (!translatedObject || typeof translatedObject !== 'object') return '';
      const record = translatedObject as Record<string, unknown>;
      const read = (key: string) => {
        const value = record[key];
        return typeof value === 'string' ? value : '';
      };
      const fallbackLanguage = applicableLocalization.defaultLanguage;
      return (
        read(language) || (fallbackLanguage ? read(fallbackLanguage) : '') || ''
      );
    },
    [language, applicableLocalization.defaultLanguage],
  );

  useEffect(() => {
    if (activeI18nLanguage() !== language) {
      void i18n.changeLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    if (i18n.options.fallbackLng !== applicableLocalization.defaultLanguage) {
      i18n.options.fallbackLng = applicableLocalization.defaultLanguage;
    }
  }, [applicableLocalization.defaultLanguage]);

  return {
    language,
    setLanguage,
    languageName: languageCodeToLanguageMap[language],
    applicableLocalization,
    setApplicableLocalization,
    applicableLanguages,
    localizedDate,
    localizedDateTime,
    translateBackendString,
  };
});
